import React, { useContext, useMemo } from 'react';
import { CountryCode } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import TicksList from '@components/dashboard/components/TicksList';
import Colors from '@components/shared/Colors';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import { CartItemType, PageSlice, PricingCompareItem, ProductCode, ScreeningType, SliceProductCompareCardsDetails } from '@contracts';
import { createCartItem } from '@utils/cart';
import { getDiscountedProductPrice } from '@utils/price';
import useProductPrices from '@utils/useProductPrices';
import AddToCart from '../AddToCart';

const pricingCompareItems: PricingCompareItem[] = [
  {
    screeningType: ScreeningType.Vms,
    productCode: ProductCode.VMSPremium,
    title: 'Microbiome + STI + HPV Screen',
    isBestValue: true,
    testFor: [
      'Lactobacilli',
      'Common BV related bacteria',
      'Candida',
      'Mycoplasma hominis',
      'Ureaplasma',
      'Gonorrhea',
      'Chlamydia',
      'Trichomoniasis',
      'Mycoplasma genitalium',
      'HPV16',
      'HPV18',
      '12 other high-risk HPV types'
    ]
  },
  {
    screeningType: ScreeningType.Hpv,
    productCode: ProductCode.VMSPremium,
    title: 'HPV + Microbiome + STI Screen',
    isBestValue: true,
    testFor: [
      'HPV16',
      'HPV18',
      '12 other high-risk HPV types',
      'Lactobacilli',
      'Common BV related bacteria',
      'Candida',
      'Mycoplasma hominis',
      'Ureaplasma',
      'Gonorrhea',
      'Chlamydia',
      'Trichomoniasis',
      'Mycoplasma genitalium'
    ]
  },
  {
    screeningType: ScreeningType.Sti,
    productCode: ProductCode.VMSPremium,
    title: 'STI + Microbiome + HPV Screen',
    isBestValue: true,
    testFor: [
      'Gonorrhea',
      'Chlamydia',
      'Trichomoniasis',
      'Mycoplasma genitalium',
      'Lactobacilli',
      'Common BV related bacteria',
      'Candida',
      'Mycoplasma hominis',
      'Ureaplasma',
      'HPV16',
      'HPV18',
      '12 other high-risk HPV types'
    ]
  },
  {
    screeningType: ScreeningType.Sti,
    productCode: ProductCode.VMSAdvanced,
    title: 'STI + Microbiome Screen',
    isMostPopular: true,
    testFor: [
      'Lactobacilli',
      'Common BV related bacteria',
      'Candida',
      'Mycoplasma hominis',
      'Ureaplasma',
      'Chlamydia',
      'Gonorrhea',
      'Trichomoniasis',
      'Mycoplasma genitalium'
    ]
  },
  {
    screeningType: ScreeningType.Vms,
    productCode: ProductCode.VMSAdvanced,
    title: 'Microbiome + STI Screen',
    isMostPopular: true,
    testFor: [
      'Chlamydia',
      'Gonorrhea',
      'Trichomoniasis',
      'Mycoplasma genitalium',
      'Lactobacilli',
      'Common BV related bacteria',
      'Candida',
      'Mycoplasma hominis',
      'Ureaplasma'
    ]
  },
  {
    productCode: ProductCode.VMSEssential,
    title: 'Microbiome Screen',
    testFor: ['Lactobacilli', 'Common BV related bacteria', 'Candida', 'Mycoplasma hominis', 'Mycoplasma genitalium', 'Ureaplasma']
  },
  {
    productCode: ProductCode.STIScreening,
    title: 'STI Screen',
    testFor: ['Chlamydia', 'Gonorrhea', 'Trichomoniasis', 'Mycoplasma genitalium']
  },
  {
    productCode: ProductCode.HPVAdvanced,
    title: 'HPV + Microbiome Screen',
    isMostPopular: true,
    testFor: [
      'HPV16',
      'HPV18',
      'Other high-risk HPV types',
      'Lactobacilli',
      'Common BV related bacteria',
      'Candida',
      'Mycoplasma hominis',
      'Ureaplasma'
    ]
  },
  {
    productCode: ProductCode.HPVScreening,
    title: 'HPV Screen',
    testFor: ['HPV16', 'HPV18', 'Other high-risk HPV types']
  }
];

interface GridProps {
  slice: PageSlice;
}

const ProductCompareCards: React.FC<GridProps> = ({ slice }) => {
  const sliceDetails = slice.primary as SliceProductCompareCardsDetails;
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { country, currency } = state;
  const sliceProducts = sliceDetails.products_to_include;
  const sliceScreenType = sliceDetails.screening_type;
  const checkScreenType = (pricingCompareItemScreenType?: ScreeningType) =>
    !sliceScreenType || !pricingCompareItemScreenType || pricingCompareItemScreenType === sliceScreenType;
  const productsToShow = pricingCompareItems.filter((item) => sliceProducts.includes(item.productCode) && checkScreenType(item.screeningType));
  const productCodes = useMemo(() => pricingCompareItems.map((item: any) => item.productCode), [pricingCompareItems]);
  const productPrices = useProductPrices(productCodes);
  const generateCartItem = (productCode: ProductCode) => {
    const cartItemPrice = Number(productPrices[productCode]);
    const cartItemEditable = true;
    const quantity = 1;

    return createCartItem(productCode, quantity, cartItemPrice, cartItemEditable, CartItemType.OneOffPurchase, undefined, undefined, []);
  };

  if (country !== CountryCode.GB) {
    return null;
  }

  return (
    <Wrapper id="product-compare-cards">
      <InnerWrap>
        <Intro>
          {sliceDetails?.heading?.text ? (
            <HeadingWrap>
              <Typography $variant={TextVariant.Heading4} $mobileVariant={TextVariant.Heading5} as="h4">
                {t(sliceDetails.heading.text)}
              </Typography>
            </HeadingWrap>
          ) : null}
        </Intro>
        <PriceComparison>
          {productsToShow.map((item, index) => (
            <PricingItem
              key={index}
              $isBestValue={item.isBestValue}
              $isMostPopular={item.isMostPopular}
              $badgeText={
                item.isBestValue || item.isMostPopular ? t(item.isBestValue ? 'components.best-value' : 'components.most-popular') : undefined
              }
            >
              <div className="title-price-wrap">
                <h4 className="product-title">{item.title}</h4>
                <PriceRow $discounted={!!getDiscountedProductPrice(item.productCode, country)}>
                  {getDiscountedProductPrice(item.productCode, country) && (
                    <NotDiscountedPrice>
                      {currency.symbol}
                      {getDiscountedProductPrice(item.productCode, country)}
                    </NotDiscountedPrice>
                  )}
                  <h5>{productPrices[item.productCode] ? `${currency.symbol}${productPrices[item.productCode]}` : ''}</h5>
                </PriceRow>
              </div>
              <AddToCart cartItem={generateCartItem(item.productCode)} />
              <div className="test-for">
                <h6>{t('founders-results.what-we-test-for')}</h6>
                <TicksList data={item.testFor} columns={1} />
              </div>
            </PricingItem>
          ))}
        </PriceComparison>
      </InnerWrap>
    </Wrapper>
  );
};

export default ProductCompareCards;

const Wrapper = styled.div`
  width: 100%;
  overflow: hidden;
`;

const InnerWrap = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  padding: 96px 64px;

  @media (max-width: 1400px) {
    gap: 48px;
    flex-direction: column;
    align-items: flex-start;
  }

  @media (max-width: 1024px) {
    align-items: center;
  }

  @media (max-width: 768px) {
    padding: 56px 16px;
  }
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 300px;

  @media (max-width: 1400px) {
    max-width: 100%;
  }

  @media (max-width: 1024px) {
    text-align: center;
  }

  @media (max-width: 500px) {
    text-align: left;
  }
`;

const HeadingWrap = styled.div``;

const PriceComparison = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  flex-direction: row-reverse;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const PricingItem = styled.div<{ $isBestValue?: boolean; $isMostPopular?: boolean; $badgeText?: string }>`
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  border-radius: 5px;
  border: 1px solid ${Colors.gold};
  color: ${Colors.navyBlue};
  border-width: ${(props) => (props.$isBestValue || props.$isMostPopular ? '2px' : '1px')};
  background: ${(props) => (props.$isMostPopular ? Colors.paleGold25 : Colors.cream)};
  position: relative;
  max-width: 300px;

  @media (max-width: 1400px) {
    width: 100%;
    max-width: 500px;
  }

  @media (max-width: 1024px) {
    padding: 16px 24px;
  }
  & > .title-price-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;

    & > h4 {
      font-family: 'BrownStd';
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 140%;
      color: inherit;
    }
  }

  & > .test-for {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;

    @media (max-width: 768px) {
      gap: 8px;
    }

    & .ticks-list-container {
      gap: 8px;

      @media (max-width: 768px) {
        align-items: flex-start;
        align-content: flex-start;
        gap: 6px;
        flex-wrap: wrap;
      }

      & .ticks-list-item {
        @media (max-width: 768px) {
          width: auto;
          gap: 4px;
        }

        & svg {
          @media (max-width: 768px) {
            width: 8px;
          }

          & path {
            fill: ${Colors.navyBlue};
          }
        }

        & p {
          font-size: 16px;

          @media (max-width: 768px) {
            font-size: 11px;
          }
        }
      }
    }

    & > h6 {
      font-family: 'BrownStd';
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 140%;
      color: inherit;

      @media (max-width: 768px) {
        font-size: 14px;
      }
    }

    ${(props) =>
      props.$badgeText &&
      `
    &::before {
      content: '${props.$badgeText}';
      position: absolute;
      top: 0;
      left: 24px;
      transform: translate3D(0, -50%, 0);
      text-align: center;
      font-family: 'BrownStd';
      font-size: 12px;
      font-style: normal;
      font-weight: 700;
      line-height: 120%;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      padding: 5px 6px 3px 8px;
      background: ${props.$isBestValue ? Colors.paleGold : Colors.lilac};
      border-radius: 2px;
      color: ${props.$isBestValue ? Colors.navyBlue : Colors.cream};
      white-space: nowrap;
    }
  `}
  }

  button {
    background: ${(props) => (props.$isBestValue || props.$isMostPopular ? Colors.lilac : Colors.gold)};
    color: ${(props) => (props.$isBestValue || props.$isMostPopular ? Colors.cream : Colors.navyBlue)};
  }
`;

const PriceRow = styled.div<{ $discounted: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  column-gap: 8px;

  & > h5 {
    font-family: 'Domaine';
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: -0.2px;
    color: ${(props) => (props.$discounted ? Colors.lilac : 'inherit')};

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
`;

const NotDiscountedPrice = styled.p`
  color: ${Colors.navyBlue};
  opacity: 50%;
  text-decoration: line-through;
  font-family: 'Domaine';
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.2px;
`;
